import React from "react";
import Link from "next/link";
import { SectionTitle, sharedStyles, placeCardStyles } from "./_shared";
import { ASSET_URL } from "../../../config";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import StarRating from "../components/StarRating";

// Grille de cards sombres pour les attractions touristiques. Autonome.
// Ne rend rien si aucune attraction (section 100% optionnelle).
export default function AttractionsGrid({ context }) {
    const { org, travel, lang } = context;

    const places = (travel?.attractionPlaces || [])
        .slice()
        .sort((a, b) => (a.position || 0) - (b.position || 0))
        .slice(0, 3);

    if (places.length === 0) return null;

    const categories = travel?.attraction?.categories || [];
    const categoryById = React.useMemo(() => {
        const map = new Map();
        for (const c of categories) map.set(c.id, c);
        return map;
    }, [categories]);

    const cityName = org.ville || org.name;
    const gridCls = `tr-pgrid tr-pgrid--${Math.min(places.length, 4)}`;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: sharedStyles + placeCardStyles }} />
            <section className="tr-ui-section">
                <SectionTitle title={t`À voir et à faire à ${cityName}`} highlight={t`À voir et à faire`} />
                <div className={gridCls}>
                    {places.map((place) => {
                        const localized = applyLang(place, lang);
                        const category = place.categoryId ? categoryById.get(place.categoryId) : null;
                        const localizedCat = category ? applyLang(category, lang) : null;
                        const imgSrc = place.image ? `${ASSET_URL}${place.image}` : null;
                        const address = place.address?.address;
                        const isInactive = place.active === false;
                        const cardCls = `tr-pcard${isInactive ? " tr-pcard--inactive" : ""}`;
                        const href = org.BASE_WEB_URL + "/travel/attractions/" + place.id;

                        const inner = (
                            <>
                                {imgSrc ? (
                                    <img src={imgSrc} alt={localized.name} className="tr-pcard__img" loading="lazy" />
                                ) : (
                                    <div className="tr-pcard__img-placeholder">
                                        {renderIcon({ name: "IconCamera", source: "tabler" }, 48)}
                                    </div>
                                )}
                                <div className="tr-pcard__wash" />
                                <div className="tr-pcard__body">
                                    {!isInactive && (
                                        <div className="tr-pcard__badges">
                                            {localizedCat?.label && (
                                                <span className="tr-pcard__badge">{localizedCat.label}</span>
                                            )}
                                            <StarRating value={place.note} size={14} />
                                        </div>
                                    )}
                                    <div className="tr-pcard__name">{localized.name}</div>
                                    {isInactive ? (
                                        <span className="tr-pcard__inactive-note">
                                            {renderIcon({ name: "IconAlertTriangle", source: "tabler" }, 14)}
                                            <Trans>Cet endroit n'est pas actif</Trans>
                                        </span>
                                    ) : address && (
                                        <div className="tr-pcard__meta">
                                            <span className="tr-pcard__meta-icon">
                                                {renderIcon({ name: "IconMapPin", source: "tabler" }, 13)}
                                            </span>
                                            <span>{address}</span>
                                        </div>
                                    )}
                                </div>
                            </>
                        );

                        if (isInactive) {
                            return <div key={place.id} className={cardCls}>{inner}</div>;
                        }
                        return (
                            <Link key={place.id} href={href} legacyBehavior>
                                <a className={cardCls}>{inner}</a>
                            </Link>
                        );
                    })}
                </div>

                <Link href={org.BASE_WEB_URL + "/travel/attractions"} legacyBehavior>
                    <a className="tr-ui-seemore">
                        <span>{t`Tout voir à ${cityName}`}</span>
                        <span className="tr-ui-seemore__arrow">
                            {renderIcon({ name: "IconChevronRight", source: "tabler" }, 16)}
                        </span>
                    </a>
                </Link>
            </section>
        </>
    );
}
